import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logsService, Log } from "../services/logs.service";
import { useSelectedColoc } from "../hooks/useSelectedColoc";
import { formatDateTime, formatCurrency } from "../utils/format";
import "../styles/LogsPage.css";

const LogsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const limit = 10;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await logsService.getLogs(page, limit);
      setLogs(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalLogs(response.meta.total);
      setLoading(false);
    } catch (err) {
      setError("Impossible de charger les journaux");
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, selectedColoc]);

  useEffect(() => {
    setPage(1);
  }, [selectedColoc]);

  const renderStatusBadge = (status: string) => {
    const labels: Record<string, string> = {
      success: "Succès",
      error: "Erreur",
      dismissed: "Rejeté",
    };
    return (
      <span className={`status-badge ${status}`}>
        {labels[status] || status}
      </span>
    );
  };

  const isClickable = (log: Log) => {
    return (log.status === "success" && log.invoiceId) || log.status === "error";
  };

  const handleLogClick = (log: Log) => {
    if (log.status === "success" && log.invoiceId) {
      navigate(`/invoices/edit/${log.invoiceId}`);
    } else if (log.status === "error") {
      navigate(`/pending-invoices/error/${log.id}`);
    }
  };

  if (loading) {
    return <div className="loading-indicator">Chargement des journaux...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="logs-container">
      <div className="logs-header-section">
        <h2>Journaux</h2>
        {selectedColoc && (
          <div className="selected-coloc-info">
            Colocation: <span className="coloc-name">{selectedColoc.name}</span>
          </div>
        )}
      </div>
      <p>Historique du traitement automatique des factures.</p>

      <div className="logs-list">
        <div className="logs-header">
          <div className="log-column date-column">Date</div>
          <div className="log-column file-column">Nom du fichier</div>
          <div className="log-column status-column">Statut</div>
          <div className="log-column provider-column">Fournisseur</div>
          <div className="log-column amount-column">Montant</div>
        </div>

        {logs.length > 0 ? (
          logs.map(log => (
            <div
              key={log.id}
              className={`log-row ${isClickable(log) ? 'clickable' : ''}`}
              onClick={() => isClickable(log) && handleLogClick(log)}
            >
              <div className="log-column date-column">{formatDateTime(log.createdAt)}</div>
              <div className="log-column file-column">{log.fileName}</div>
              <div className="log-column status-column">{renderStatusBadge(log.status)}</div>
              <div className="log-column provider-column">
                {log.status === "success" ? log.providerName : log.status === "dismissed" ? '-' : (log.errorMessage || '-')}
              </div>
              <div className="log-column amount-column">
                {log.status === "success" && log.amount != null ? formatCurrency(log.amount) : '-'}
              </div>
            </div>
          ))
        ) : (
          <div className="no-logs-message">
            Aucun journal disponible pour le moment.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Precedent
          </button>

          <span className="pagination-info">
            Page {page} sur {totalPages} ({totalLogs} journaux)
          </span>

          <button
            className="pagination-button"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default LogsPage;
